import React, { useEffect, useState } from 'react';
import { DateBadge, Spinner, StatCard } from '../components/base';
import { STAGES } from '../lib/constantes';
import { useEmpresa } from '../lib/empresas';
import { auth, db } from '../lib/firebase';
import { calcContractTotal, fmt, fmtDateOnly } from '../lib/formato';
import { FORA_DO_PIPELINE } from '../lib/pipeline';

const MARCA = {
  red:    { faixa:'border-t-[3px] border-red-600',    selo:'bg-red-600 text-white',    titulo:'text-red-700',    icone:'text-red-500'    },
  orange: { faixa:'border-t-[3px] border-orange-500', selo:'bg-orange-500 text-white', titulo:'text-orange-700', icone:'text-orange-500' },
  blue:   { faixa:'border-t-[3px] border-blue-600',   selo:'bg-blue-600 text-white',   titulo:'text-blue-700',   icone:'text-blue-500'   },
};

function PartnerPortalView({ partner, leads, companyId }) {
  // O nome e a cor da empresa vêm do banco. O parceiro entra anônimo, então o
  // documento da empresa é de leitura pública; ele só guarda marca, nada mais.
  const { empresa: company } = useEmpresa(companyId);
  const marca     = MARCA[company?.color] || MARCA.blue;
  // Presença e dinheiro são filtros diferentes: o lead em repescagem continua aparecendo
  // na lista de andamento, mas o valor dele não entra no Pipeline Total, porque é dinheiro
  // já dado como perdido. Mesma regra da tela interna.
  const pipeline  = leads.filter(l => !['fechado','perdido'].includes(l.stage));
  const noDinheiro = pipeline.filter(l => !FORA_DO_PIPELINE.includes(l.stage));
  const closed    = leads.filter(l => l.stage === 'fechado');
  const totalPipelineVal = noDinheiro.reduce((s,l) => s + calcContractTotal(l.value, l.recurrence), 0);
  const totalClosedVal   = closed.reduce((s,l)   => s + calcContractTotal(l.value, l.recurrence), 0);

  const INTER_ICONS = { ligacao:'📞', email:'📧', reuniao:'🤝', whatsapp:'💬', outro:'📝' };

  return <div className="min-h-screen bg-gray-100">
    {/* Header */}
    <div className={`glass border-b border-black/[0.07] sticky top-0 z-10 ${marca.faixa}`}>
      <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-400 font-semibold mb-0.5">Portal do Parceiro</div>
          <div className="text-xl font-bold text-gray-800">{partner.name}</div>
          {partner.email && <div className="text-xs text-gray-400 mt-0.5">{partner.email}</div>}
        </div>
        <div className="text-right">
          <div className={`text-sm font-bold px-3 py-1 rounded-full ${company ? marca.selo : 'bg-gray-100 text-gray-500'}`}>
            {company?.label || companyId}
          </div>
          <div className="text-xs text-gray-400 mt-1">{new Date().toLocaleDateString('pt-BR', {day:'2-digit',month:'long',year:'numeric'})}</div>
        </div>
      </div>
    </div>

    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total de Leads"  value={leads.length} />
        <StatCard label="Em andamento"    value={pipeline.length} color="text-blue-700" />
        <StatCard label="✅ Fechados"     value={closed.length}   color="text-green-700" />
      </div>
      {(totalPipelineVal > 0 || totalClosedVal > 0) && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="💰 Pipeline Total"   value={fmt(totalPipelineVal)} color="text-blue-700" />
          <StatCard label="🏆 Receita Fechada"  value={fmt(totalClosedVal)}   color="text-green-700" />
        </div>
      )}

      {/* Leads em andamento */}
      {pipeline.length > 0 && (
        <div className="acard rounded-2xl p-5">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className={marca.icone}>📊</span> Leads em Andamento
          </h3>
          <div className="space-y-3">
            {pipeline.map(l => {
              const stage = STAGES.find(s => s.key === l.stage);
              const contractTotal = calcContractTotal(l.value, l.recurrence);
              const hasTotal = contractTotal !== (Number(l.value)||0);
              const lastInter = l.interactions?.length
                ? [...l.interactions].sort((a,b)=>(b.created_at||b.date||'')>(a.created_at||a.date||'')?1:-1)[0]
                : null;
              return <div key={l.id} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 truncate">{l.company_name}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${stage?.color||'bg-gray-100 border-gray-200'}`}>{stage?.label||l.stage}</span>
                      {l.next_contact_date && <DateBadge dateStr={l.next_contact_date} />}
                    </div>
                    {lastInter && <div className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
                      <span>{INTER_ICONS[lastInter.type]||'📝'}</span>
                      <span>{fmtDateOnly(lastInter.date||lastInter.created_at?.slice(0,10))}</span>
                      {lastInter.note && <span className="truncate max-w-[160px]">· {lastInter.note}</span>}
                    </div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {l.value > 0 && <div className="font-semibold text-gray-700">{fmt(l.value)}{l.recurrence && l.recurrence!=='unica' ? '/mês' : ''}</div>}
                    {hasTotal && <div className="text-xs text-blue-600 font-medium">Total: {fmt(contractTotal)}</div>}
                  </div>
                </div>
              </div>;
            })}
          </div>
        </div>
      )}

      {/* Leads fechados */}
      {closed.length > 0 && (
        <div className="acard rounded-2xl p-5">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-green-500">✅</span> Clientes Fechados
          </h3>
          <div className="space-y-3">
            {closed.map(l => {
              const contractTotal = calcContractTotal(l.value, l.recurrence);
              const hasTotal = contractTotal !== (Number(l.value)||0);
              const myCommissionRate = l.partners?.find(p=>p.id===partner.id)?.commission_rate || 0;
              const commissionValue  = myCommissionRate ? (contractTotal * myCommissionRate / 100) : null;
              return <div key={l.id} className="bg-green-50 border border-green-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-green-800 truncate">✅ {l.company_name}</div>
                    {(l.closed_at || l.stage_changed_at || l.updated_at) && <div className="text-xs text-green-500 mt-0.5">Fechado em {new Date(l.closed_at || l.stage_changed_at || l.updated_at).toLocaleDateString('pt-BR')}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {l.value > 0 && <div className="font-semibold text-green-700">{fmt(l.value)}{l.recurrence && l.recurrence!=='unica' ? '/mês' : ''}</div>}
                    {hasTotal && <div className="text-xs text-green-600 font-medium">Total: {fmt(contractTotal)}</div>}
                  </div>
                </div>
                {commissionValue && (
                  <div className="mt-2 flex items-center justify-between bg-green-100 rounded-lg px-3 py-1.5">
                    <span className="text-xs text-green-600 font-medium">{myCommissionRate}% comissão{hasTotal ? ' sobre contrato total' : ''}</span>
                    <span className="text-sm font-bold text-green-700">{fmt(commissionValue)}</span>
                  </div>
                )}
              </div>;
            })}
          </div>
        </div>
      )}

      {leads.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p className="text-base">Nenhum lead vinculado ainda</p>
        </div>
      )}

      <p className="text-center text-xs text-gray-300 pb-4">Portal exclusivo · {partner.name} · {new Date().toLocaleDateString('pt-BR')}</p>
    </div>
  </div>;
}

// ─── PartnerPortalApp ─────────────────────────────────────

function PartnerPortalApp({ token }) {
  const [status, setStatus]   = useState('loading'); // loading | ready | error
  const [partner, setPartner] = useState(null);
  const [leads,   setLeads]   = useState([]);
  const [companyId, setCompanyId] = useState(null);

  // Tempo real (roadmap #20): o parceiro vê a mudança de etapa sem recarregar
  useEffect(() => {
    let vivo = true, pararLeads = null, pararParceiro = null;
    auth.signInAnonymously()
      .then(() => db.collection('portals').doc(token).get())
      .then(doc => {
        if (!vivo) return;
        if (!doc.exists) { setStatus('error'); return; }
        const { partner_id, company_id } = doc.data();
        setCompanyId(company_id);
        const co = db.collection('companies').doc(company_id);
        pararParceiro = co.collection('partners').doc(partner_id).onSnapshot(
          pDoc => { if (!pDoc.exists) setStatus('error'); else setPartner({ id: pDoc.id, ...pDoc.data() }); },
          () => setStatus('error'));
        pararLeads = co.collection('leads').onSnapshot(snap => {
          setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(l => l.partners?.some(lp => lp.id === partner_id)));
          setStatus(s => s === 'error' ? s : 'ready');
        }, () => setStatus('error'));
      })
      .catch(() => { if (vivo) setStatus('error'); });
    return () => { vivo = false; if (pararLeads) pararLeads(); if (pararParceiro) pararParceiro(); };
  }, [token]);

  if (status === 'loading') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col gap-3">
      <Spinner />
      <p className="text-sm text-gray-400">Carregando portal…</p>
    </div>
  );
  if (status === 'error') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center space-y-2">
        <div className="text-5xl">🔒</div>
        <p className="text-gray-600 font-medium">Link inválido ou expirado</p>
        <p className="text-sm text-gray-400">Solicite um novo link ao seu contato.</p>
      </div>
    </div>
  );
  return <PartnerPortalView partner={partner} leads={leads} companyId={companyId} />;
}

export { PartnerPortalApp, PartnerPortalView };
